import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../config/supabase';
import { track } from '../utils/analytics';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signInWithMagicLink: (email: string) => Promise<void>;
  verifyOtp: (email: string, token: string) => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signUpWithPassword: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  connectGoogleCalendar: (returnTo?: 'settings' | 'onboarding') => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  passwordRecovery: boolean;
  clearPasswordRecovery: () => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  const clearPasswordRecovery = () => setPasswordRecovery(false);

  useEffect(() => {
    // Detect password reset redirect via query param or URL hash
    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash;
    if (params.get('reset-password') === 'true' || hash.includes('type=recovery')) {
      setPasswordRecovery(true);
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (event === 'PASSWORD_RECOVERY') {
        setPasswordRecovery(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Google Calendar OAuth callback: exchange the auth code for the CURRENT user.
  // This is a pure authorization flow (not a Supabase sign-in), so the session is
  // never touched — even when the authorized Google account is a different Mozayc user.
  const gcalExchangeRan = useRef(false);
  useEffect(() => {
    if (!user || gcalExchangeRan.current) return;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const stored = sessionStorage.getItem('gcal-oauth');
    if (!code || !stored) return;

    let parsed: { nonce: string; returnTo: 'settings' | 'onboarding' };
    try { parsed = JSON.parse(stored); } catch { sessionStorage.removeItem('gcal-oauth'); return; }
    if (state !== parsed.nonce) { sessionStorage.removeItem('gcal-oauth'); return; } // CSRF check

    gcalExchangeRan.current = true;
    const origin = window.location.origin;
    const ok = parsed.returnTo === 'onboarding' ? `${origin}/app` : `${origin}/app?return=settings-gcal`;
    const err = parsed.returnTo === 'onboarding' ? `${origin}/app` : `${origin}/app?return=settings-gcal&gcal-error=1`;

    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('google-calendar-sync', {
          body: { action: 'exchange-code', code, redirectUri: `${origin}/app` },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        track('calendar_imported', { source: 'google' });
        sessionStorage.removeItem('gcal-oauth');
        window.location.replace(ok);
      } catch (e) {
        console.error('Google Calendar connect failed:', e);
        sessionStorage.removeItem('gcal-oauth');
        window.location.replace(err);
      }
    })();
  }, [user]);

  const signInWithMagicLink = async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: true, emailRedirectTo: `${window.location.origin}/app` },
    });
    if (error) throw error;
  };

  const verifyOtp = async (email: string, token: string) => {
    const { error } = await supabase.auth.verifyOtp({ email, token, type: 'email' });
    if (error) throw error;
  };

  const signInWithPassword = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signUpWithPassword = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: `${window.location.origin}/app` },
    });
    if (error) throw error;
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/app`,
      },
    });
    if (error) throw error;
  };

  // Pure Google authorization (NOT a Supabase sign-in): obtain a calendar refresh
  // token for the currently logged-in user. The code is exchanged on return by the
  // OAuth callback effect above. This never changes the active session.
  const connectGoogleCalendar = async (returnTo: 'settings' | 'onboarding' = 'settings') => {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) throw new Error('Missing VITE_GOOGLE_CLIENT_ID');

    const nonce = crypto.randomUUID();
    sessionStorage.setItem('gcal-oauth', JSON.stringify({ nonce, returnTo }));

    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: `${window.location.origin}/app`,
      response_type: 'code',
      scope: 'https://www.googleapis.com/auth/calendar',
      access_type: 'offline',
      prompt: 'consent',
      state: nonce,
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/app?reset-password=true`,
    });
    if (error) throw error;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signInWithMagicLink, verifyOtp, signInWithPassword, signUpWithPassword, signInWithGoogle, connectGoogleCalendar, resetPassword, passwordRecovery, clearPasswordRecovery, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
